import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { collection, doc, getDocs, updateDoc, setDoc, deleteField, Timestamp } from 'firebase/firestore'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import type { ConfigCatalogs, CarriersCatalog, PayTo } from '@/types/config';
import type { ChargeCategory, ChargeCategoriesCompletenessConfig } from '@/domain/types/configs/company/ChargeCategoriesConfig'
import type { LiquidationConfig } from '@/domain/types/configs/company/LiquidationConfig'
import { chargeCategoriesConfig } from '@/infrastructure/config/company/charge-categories.config'
import { liquidationConfig } from '@/infrastructure/config/company/liquidation.config'
import { shipmentStatusConfig } from '@/infrastructure/config/company/shipment-status.config'

  const catalogMapping = {
    'SERVICE_TYPE_CATALOG': 'serviceType',
    'SHIPMENT_TYPE_CATALOG': 'shipmentType',
    'INCOTERMS_CATALOG': 'incoterms',
    'INTERNATIONAL_CARRIER': 'internationalCarrier',
    'MODE_OF_TRANSPORT': 'modeOfTransport',
    'RATE_CATEGORY_CATALOG': 'rateCategory',
    'CHARGES_CATALOG': 'chargesType',
    'CARRIERS_CATALOG': 'carriers',
    'SHIPMENT_STATUS': 'status'
  } as const

const getCarrierItems = (data: CarriersCatalog | undefined, section: 'transporters' | 'payTo'): PayTo[] => {
  if (!data || !data[section]) return []
  return data[section].map(item => ({
    ...item,
    isActived: item.isActived ?? false,
    trackCarrier: item.trackCarrier ?? null
  }))
}

export const useCatalogsStore = defineStore('catalogs', () => {
  const loading = ref(false)
  const error = ref<string | null>(null)
  const configs = ref<ConfigCatalogs | null>(null)

  const getMergedCarriers = (data: CarriersCatalog) => {
    if (!data || !data.transporters || !data.payTo) {
      console.warn('Datos de carriers incompletos:', data)
      return []
    }

    const carriers = new Map()

    data.transporters.forEach(transporter => {
      if (!transporter.value) {
        console.warn('Transportista sin valor:', transporter)
        return
      }
      carriers.set(transporter.value, {
        ...transporter,
        isTransporter: transporter.visible,
        isPayTo: false,
        visible: transporter.visible
      })
    })

    data.payTo.forEach(payTo => {
      if (!payTo.value) {
        console.warn('PayTo sin valor:', payTo)
        return
      }
      if (carriers.has(payTo.value)) {
        const existing = carriers.get(payTo.value)
        carriers.set(payTo.value, {
          ...existing,
          isPayTo: payTo.visible,
          visible: payTo.visible
        })
      } else {
        carriers.set(payTo.value, {
          ...payTo,
          isTransporter: false,
          isPayTo: payTo.visible,
          visible: payTo.visible
        })
      }
    })

    return Array.from(carriers.values())
  }

  const getItems = computed(() => {
    return (configKey: keyof ConfigCatalogs, itemsKey?: 'transporters' | 'payTo') => {
      
      if (!configs.value?.[configKey]) {
        return []
      }
      
      const data = configs.value[configKey]
      if (!data) {
        return []
      }

      // Manejo especial para CARRIERS_CATALOG
      if (configKey === 'CARRIERS_CATALOG') {
        if (itemsKey) {
          return getCarrierItems(data as CarriersCatalog, itemsKey)
        }
        return getMergedCarriers(data as CarriersCatalog)
      }

      const catalogKey = catalogMapping[configKey as keyof typeof catalogMapping] || itemsKey
      if (!catalogKey) {
        return []
      }

      let catalogItems: any[] = []
      
      if (configKey === 'CHARGES_CATALOG') {
        catalogItems = (data as any).chargesType || []
      } else if (configKey === 'INTERNATIONAL_CARRIER') {
        catalogItems = (data as any).internationalCarrier || []
      } else {
        catalogItems = (data as any)[catalogKey] || []
      }

      // Asegurar que todos los items tengan visible por defecto en true
      return catalogItems.map((item: any) => ({
        ...item,
        visible: item.visible ?? true,
        // Mantener campos especiales si existen
        ...(item.canTrack !== undefined ? { canTrack: item.canTrack } : {}),
        ...(item.trackCarrier !== undefined ? { trackCarrier: item.trackCarrier } : {})
      }))
    }
  })

  const getTransportersCount = computed(() => {
    return configs.value?.CARRIERS_CATALOG?.transporters?.filter(t => t.visible)?.length || 0
  })

  const getPayToCount = computed(() => {
    return configs.value?.CARRIERS_CATALOG?.payTo?.filter(p => p.visible)?.length || 0
  })

  const getMetadata = computed(() => {
    return (configKey: keyof ConfigCatalogs) => {
      const data = configs.value?.[configKey]
      if (!data) return undefined

      return {
        updatedAt: (data as any).updatedAt,
        createdAt: (data as any).createdAt,
        createdBy: (data as any).createdBy
      }
    }
  })

  const getShowChargesCatalog = computed(() => {
    return configs.value?.CHARGES_CATALOG?.showChargesCatalog
  })

  const getShowAll = computed(() => {
    return (configKey: keyof ConfigCatalogs) => {
      const data = configs.value?.[configKey]
      return data ? (data as any).showAll : undefined
    }
  })

  const getChargeCategories = computed(() => {
    if (!configs.value) {
      return [...chargeCategoriesConfig.categories].sort((a, b) => a.order - b.order)
    }
    const config = configs.value.CHARGE_CATEGORIES_CONFIG
    if (!config?.categories?.length) {
      return [...chargeCategoriesConfig.categories].sort((a, b) => a.order - b.order)
    }
    return [...config.categories].sort((a, b) => a.order - b.order)
  })

  const getActiveChargeCategories = computed(() => {
    return getChargeCategories.value.filter(c => c.isActive)
  })

  /**
   * Configuración del motor de Liquidación, con el fallback genérico debajo: una
   * compañía que solo configuró un grupo sigue recibiendo los defaults del
   * producto en los demás, nunca `undefined`.
   */
  const getLiquidationConfig = computed<LiquidationConfig>(() => {
    const stored = configs.value?.LIQUIDATION_CONFIG
    if (!stored) return liquidationConfig
    return {
      trm: { ...liquidationConfig.trm, ...stored.trm },
      seguro: { ...liquidationConfig.seguro, ...stored.seguro },
      agenciasAduana: stored.agenciasAduana ?? liquidationConfig.agenciasAduana,
      impuestos: { ...liquidationConfig.impuestos, ...stored.impuestos },
      depositosGarantia: { ...liquidationConfig.depositosGarantia, ...stored.depositosGarantia },
      factorImportacion: { ...liquidationConfig.factorImportacion, ...stored.factorImportacion },
      extraccion: { ...liquidationConfig.extraccion, ...stored.extraccion },
      documento: { ...liquidationConfig.documento, ...stored.documento },
    }
  })

  /** Parámetros 32-33: gobierno de las categorías obligatorias. */
  const getChargeCategoriesCompleteness = computed<ChargeCategoriesCompletenessConfig>(() => {
    return configs.value?.CHARGE_CATEGORIES_CONFIG?.completeness ?? {
      behaviorIfMissing: 'alerta_informativa',
      validationMoment: 'al_cerrar_liquidacion',
    }
  })

  /**
   * Campos que la compañía exige en cada cargo. Vacío por defecto: la validación
   * se estrena cuando alguien la configura, no cuando se despliega la versión.
   */
  const getRequiredChargeFields = computed<string[]>(
    () => configs.value?.CHARGE_CATEGORIES_CONFIG?.requiredChargeFields ?? []
  )

  /** Si la liquidación muestra el apartado Total Gastos en Destino. */
  const getDestinationTotalEnabled = computed<boolean>(
    () => configs.value?.CHARGE_CATEGORIES_CONFIG?.destinationTotalEnabled === true
  )

  /** Si el seguro presuntivo suma al Total Gastos en Destino (la marca, sin mirar el modo del seguro). */
  const getDestinationTotalIncludesSeguroPresuntivo = computed<boolean>(
    () => configs.value?.CHARGE_CATEGORIES_CONFIG?.destinationTotalIncludesSeguroPresuntivo === true
  )

  const loadConfigs = async (companyId: string) => {
    if (!companyId) {
      error.value = 'No hay una compañía seleccionada'
      return
    }

    try {
      loading.value = true
      error.value = null
      
      const db = FirebaseProvider.getInstance().getFirestore()
      const configRef = collection(db, `Companies/${companyId}/config`)
      const configSnap = await getDocs(configRef)

      if (!configSnap.empty) {
        const configData = configSnap.docs.reduce((acc: any, doc) => {
          acc[doc.id] = doc.data()
          return acc
        }, {}) as ConfigCatalogs
        
        configs.value = configData
      } else {
        throw new Error('No se encontró la configuración para esta compañía')
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Error desconocido'
      console.error('Error al cargar la configuración:', e)
    } finally {
      loading.value = false
    }
  }

  const toggleItem = async (
    companyId: string, 
    configKey: keyof ConfigCatalogs, 
    item: { value: string; label: string; visible?: boolean }, 
    itemsKey?: 'transporters' | 'payTo',
    field: 'isActived' | 'visible' = 'visible'
  ) => {
    
    if (!companyId) throw new Error('No hay una compañía seleccionada')
    if (!configs.value) throw new Error('No hay configuración cargada')
    if (!configs.value[configKey]) throw new Error(`No hay configuración para ${configKey}`)

    // Manejo especial para CARRIERS_CATALOG
    if (configKey === 'CARRIERS_CATALOG' && itemsKey) {
      const section = itemsKey // 'transporters' o 'payTo'
      const carrierConfig = configs.value.CARRIERS_CATALOG
      if (!carrierConfig) throw new Error('No hay configuración de transportistas')
      
      const targetArray = [...(carrierConfig[section] || [])]
      const itemIndex = targetArray.findIndex(i => i.value === item.value)

      // Crear copia del estado actual para poder revertir si hay error
      const previousState = JSON.parse(JSON.stringify(configs.value))

      try {
        if (itemIndex === -1) {
          // Si el item no existe, lo agregamos
          targetArray.push({
            value: item.value,
            label: item.label,
            visible: true,
            isActived: true,
            trackCarrier: null
          })
        } else {
          // Si existe, actualizamos solo su visibilidad
          const currentItem = targetArray[itemIndex]
          currentItem.visible = !currentItem.visible
        }

        // Actualizar estado local
        const now = new Date()
        configs.value = {
          ...configs.value,
          CARRIERS_CATALOG: {
            ...carrierConfig,
            [section]: targetArray,
            updatedAt: {
              seconds: Math.floor(now.getTime() / 1000),
              nanoseconds: (now.getTime() % 1000) * 1000000
            }
          }
        }

        // Actualizar Firebase
        const db = FirebaseProvider.getInstance().getFirestore()
        const catalogRef = doc(db, `Companies/${companyId}/config/CARRIERS_CATALOG`)
        await updateDoc(catalogRef, {
          [section]: targetArray,
          updatedAt: Timestamp.fromDate(now)
        })

        return
      } catch (error) {
        configs.value = previousState
        throw error
      }
    }

    // Manejo normal para otros catálogos
    const arrayKey = catalogMapping[configKey as keyof typeof catalogMapping] || itemsKey
    if (!arrayKey) throw new Error('No se pudo determinar el array a actualizar')

    const currentData = configs.value[configKey]
    if (!currentData || !(arrayKey in currentData)) {
      throw new Error('No se encontraron datos para actualizar')
    }

    const currentArray = (currentData as any)[arrayKey]
    if (!Array.isArray(currentArray)) {
      throw new Error('El valor no es un array')
    }

    const itemToUpdate = currentArray.find(currentItem => currentItem.value === item.value)
    if (!itemToUpdate) {
      throw new Error('No se encontró el item a actualizar')
    }

    // Crear copia del estado actual para poder revertir si hay error
    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      // Actualizar estado local inmediatamente (optimista)
      const updatedItems = currentArray.map(currentItem => {
        if (currentItem.value === item.value) {
          return {
            ...currentItem,
            [field]: !currentItem[field]
          }
        }
        return currentItem
      })

      // Actualizar estado local
      const now = new Date()
      configs.value = {
        ...configs.value,
        [configKey]: {
          ...configs.value[configKey],
          [arrayKey]: updatedItems,
          updatedAt: {
            seconds: Math.floor(now.getTime() / 1000),
            nanoseconds: (now.getTime() % 1000) * 1000000
          }
        }
      }

      // Actualizar Firebase
      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/${configKey}`)
      await updateDoc(catalogRef, {
        [arrayKey]: updatedItems,
        updatedAt: Timestamp.fromDate(now)
      })

    } catch (error) {
      // Si hay error, revertir al estado anterior
      configs.value = previousState
      console.error('Error en toggleItem:', error)
      throw error
    }
  }

  const updateCarrier = async (
    companyId: string,
    section: 'transporters' | 'payTo',
    item: { value: string; label: string; isActived?: boolean }
  ) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')
    if (!configs.value?.CARRIERS_CATALOG) throw new Error('No hay configuración de transportistas')

    const previousState = JSON.parse(JSON.stringify(configs.value))
    const carrierConfig = configs.value.CARRIERS_CATALOG

    try {
      // Obtener el array actual
      const currentArray = [...(carrierConfig[section] || [])]
      const existingItem = currentArray.find(i => i.value === item.value)

      let updatedArray: PayTo[]
      if (existingItem) {
        // Actualizar item existente
        updatedArray = currentArray.map(current => 
          current.value === item.value 
            ? { ...current, isActived: !current.isActived }
            : current
        )
      } else {
        // Agregar nuevo item
        updatedArray = [
          ...currentArray,
          {
            value: item.value,
            label: item.label,
            isActived: true,
            visible: true,
            trackCarrier: null
          }
        ]
      }

      // Actualizar estado local
      const now = new Date()
      const timestamp = {
        seconds: Math.floor(now.getTime() / 1000),
        nanoseconds: (now.getTime() % 1000) * 1000000
      }

      configs.value = {
        ...configs.value,
        CARRIERS_CATALOG: {
          ...carrierConfig,
          [section]: updatedArray,
          updatedAt: timestamp
        }
      }

      // Actualizar Firebase
      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/CARRIERS_CATALOG`)
      
      await updateDoc(catalogRef, {
        [section]: updatedArray,
        updatedAt: Timestamp.fromDate(now)
      })

    } catch (error) {
      configs.value = previousState
      console.error('Error al actualizar carrier:', error)
      throw error
    }
  }

  const toggleVisibility = async (companyId: string) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')

    try {
      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/CHARGES_CATALOG`)

      const newShowChargesCatalog = !getShowChargesCatalog.value

      await updateDoc(catalogRef, {
        showChargesCatalog: newShowChargesCatalog,
        updatedAt: new Date()
      })

      if (configs.value?.CHARGES_CATALOG) {
        configs.value = {
          ...configs.value,
          CHARGES_CATALOG: {
          ...configs.value.CHARGES_CATALOG,
          showChargesCatalog: newShowChargesCatalog
          }
        }
      }
    } catch (error) {
      console.error('Error al actualizar la visibilidad:', error)
      throw error
    }
  }

  const updateCatalogItem = async ({ companyId, configKey, item, itemsKey, field = 'isActived' }: UpdateCatalogParams) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')
    if (!configs.value?.[configKey]) throw new Error(`No hay configuración para ${configKey}`)

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      if (configKey === 'CARRIERS_CATALOG' && itemsKey) {
        return await updateCarrierItem({
          companyId,
          section: itemsKey as 'transporters' | 'payTo',
          carrier: item
        })
      }

      const arrayKey = catalogMapping[configKey as keyof typeof catalogMapping] || itemsKey
      if (!arrayKey) throw new Error('No se pudo determinar el array a actualizar')

      const currentData = configs.value[configKey]
      if (!currentData || !(arrayKey in currentData)) {
        throw new Error('No se encontraron datos para actualizar')
      }

      const currentArray = (currentData as any)[arrayKey]
      if (!Array.isArray(currentArray)) {
        throw new Error('El valor no es un array')
      }

      const updatedItems = currentArray.map(currentItem => {
        if (currentItem.value === item.value) {
          return {
            ...currentItem,
            [field]: !currentItem[field]
          }
        }
        return currentItem
      })

      const now = new Date()
      const timestamp = {
        seconds: Math.floor(now.getTime() / 1000),
        nanoseconds: (now.getTime() % 1000) * 1000000
      }

      // Actualizar estado local
      configs.value = {
        ...configs.value,
        [configKey]: {
          ...configs.value[configKey],
          [arrayKey]: updatedItems,
          updatedAt: timestamp
        }
      }

      // Actualizar Firebase
      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/${configKey}`)
      await updateDoc(catalogRef, {
        [arrayKey]: updatedItems,
        updatedAt: Timestamp.fromDate(now)
      })

    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  const updateCarrierItem = async ({ companyId, section, carrier }: UpdateCarrierParams) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')
    if (!configs.value?.CARRIERS_CATALOG) throw new Error('No hay configuración de transportistas')

    const previousState = JSON.parse(JSON.stringify(configs.value))
    const carrierConfig = configs.value.CARRIERS_CATALOG

    try {
      const currentArray = [...(carrierConfig[section] || [])]
      const existingItem = currentArray.find(i => i.value === carrier.value)

      let updatedArray: PayTo[]
      if (existingItem) {
        updatedArray = currentArray.map(current => 
          current.value === carrier.value 
            ? { ...current, isActived: !current.isActived }
            : current
        )
      } else {
        updatedArray = [
          ...currentArray,
          {
            value: carrier.value,
            label: carrier.label,
            isActived: true,
            visible: true,
            trackCarrier: carrier.trackCarrier ?? null
          }
        ]
      }

      const now = new Date()
      const timestamp = {
        seconds: Math.floor(now.getTime() / 1000),
        nanoseconds: (now.getTime() % 1000) * 1000000
      }

      // Actualizar estado local
      configs.value = {
        ...configs.value,
        CARRIERS_CATALOG: {
          ...carrierConfig,
          [section]: updatedArray,
          updatedAt: timestamp
        }
      }

      // Actualizar Firebase
      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/CARRIERS_CATALOG`)
      await updateDoc(catalogRef, {
        [section]: updatedArray,
        updatedAt: Timestamp.fromDate(now)
      })

    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  const updateStatusOrder = async (companyId: string, statuses: any[]) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')
    if (!configs.value?.SHIPMENT_STATUS) throw new Error('No hay configuración de estados')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const updatedStatuses = statuses.map((status, index) => ({
        ...status,
        order: index
      }))

      const now = new Date()
      const timestamp = {
        seconds: Math.floor(now.getTime() / 1000),
        nanoseconds: (now.getTime() % 1000) * 1000000
      }

      configs.value = {
        ...configs.value,
        SHIPMENT_STATUS: {
          ...configs.value.SHIPMENT_STATUS,
          status: updatedStatuses,
          updatedAt: timestamp
        }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/SHIPMENT_STATUS`)
      await updateDoc(catalogRef, {
        status: updatedStatuses,
        updatedAt: Timestamp.fromDate(now)
      })

    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  const updateStatusOrderByMode = async (companyId: string, mode: 'AIR' | 'SEA' | 'ROA', statuses: any[]) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')
    if (!configs.value?.SHIPMENT_STATUS) throw new Error('No hay configuración de estados')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const updatedStatuses = statuses.map((status, index) => {
        const cleaned: Record<string, any> = {}
        for (const [key, value] of Object.entries(status)) {
          if (value !== undefined) cleaned[key] = value
        }
        cleaned.order = index
        return cleaned
      })

      const now = new Date()
      const timestamp = {
        seconds: Math.floor(now.getTime() / 1000),
        nanoseconds: (now.getTime() % 1000) * 1000000
      }

      // Inicializar statusByMode si no existe
      const statusByMode: any = configs.value.SHIPMENT_STATUS.statusByMode || {
        AIR: configs.value.SHIPMENT_STATUS.status || [],
        SEA: configs.value.SHIPMENT_STATUS.status || [],
        ROA: []
      }

      // Actualizar solo el modo seleccionado
      statusByMode[mode] = updatedStatuses

      configs.value = {
        ...configs.value,
        SHIPMENT_STATUS: {
          ...configs.value.SHIPMENT_STATUS,
          statusByMode,
          updatedAt: timestamp
        }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/SHIPMENT_STATUS`)
      await updateDoc(catalogRef, {
        [`statusByMode.${mode}`]: updatedStatuses,
        updatedAt: Timestamp.fromDate(now)
      })

    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  // Conjuntos de estados por campo (SHIPMENT_STATUS.statusSets)
  type StatusSetMeta = { label: string; field: string; value: string }

  const cleanStatusSetStatuses = (statuses: any[]) =>
    statuses.map((status, index) => {
      const cleaned: Record<string, any> = {}
      for (const [key, value] of Object.entries(status)) {
        if (value !== undefined) cleaned[key] = value
      }
      cleaned.order = index
      return cleaned
    })

  const nowTimestamp = (now: Date) => ({
    seconds: Math.floor(now.getTime() / 1000),
    nanoseconds: (now.getTime() % 1000) * 1000000
  })

  const createStatusSet = async (companyId: string, setId: string, meta: StatusSetMeta, statuses: any[]) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')
    if (!configs.value?.SHIPMENT_STATUS) throw new Error('No hay configuración de estados')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const newSet = {
        label: meta.label,
        field: meta.field,
        value: meta.value,
        statuses: cleanStatusSetStatuses(statuses) as any
      }

      const now = new Date()
      configs.value = {
        ...configs.value,
        SHIPMENT_STATUS: {
          ...configs.value.SHIPMENT_STATUS,
          statusSets: { ...(configs.value.SHIPMENT_STATUS.statusSets || {}), [setId]: newSet },
          updatedAt: nowTimestamp(now)
        }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/SHIPMENT_STATUS`)
      await updateDoc(catalogRef, {
        [`statusSets.${setId}`]: newSet,
        updatedAt: Timestamp.fromDate(now)
      })

    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  const updateStatusSetStatuses = async (companyId: string, setId: string, statuses: any[]) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')
    if (!configs.value?.SHIPMENT_STATUS) throw new Error('No hay configuración de estados')
    const currentSet = configs.value.SHIPMENT_STATUS.statusSets?.[setId]
    if (!currentSet) throw new Error('El conjunto de estados no existe')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const updatedStatuses = cleanStatusSetStatuses(statuses)

      const now = new Date()
      configs.value = {
        ...configs.value,
        SHIPMENT_STATUS: {
          ...configs.value.SHIPMENT_STATUS,
          statusSets: {
            ...(configs.value.SHIPMENT_STATUS.statusSets || {}),
            [setId]: { ...currentSet, statuses: updatedStatuses as any }
          },
          updatedAt: nowTimestamp(now)
        }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/SHIPMENT_STATUS`)
      await updateDoc(catalogRef, {
        [`statusSets.${setId}.statuses`]: updatedStatuses,
        updatedAt: Timestamp.fromDate(now)
      })

    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  const deleteStatusSet = async (companyId: string, setId: string) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')
    if (!configs.value?.SHIPMENT_STATUS) throw new Error('No hay configuración de estados')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const { [setId]: _removed, ...remainingSets } = configs.value.SHIPMENT_STATUS.statusSets || {}

      const now = new Date()
      configs.value = {
        ...configs.value,
        SHIPMENT_STATUS: {
          ...configs.value.SHIPMENT_STATUS,
          statusSets: remainingSets,
          updatedAt: nowTimestamp(now)
        }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/SHIPMENT_STATUS`)
      await updateDoc(catalogRef, {
        [`statusSets.${setId}`]: deleteField(),
        updatedAt: Timestamp.fromDate(now)
      })

    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  /** Campo que define los estados de la compañía. Las listas de otros campos quedan guardadas e inactivas. */
  /** Si la compañía no tiene SHIPMENT_STATUS, lo crea con los estados por defecto de la app para los 3 modos. */
  const ensureShipmentStatusConfig = async (companyId: string) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')
    if (configs.value?.SHIPMENT_STATUS) return

    const statusByMode = {
      AIR: cleanStatusSetStatuses(shipmentStatusConfig.statusByMode?.AIR ?? []),
      SEA: cleanStatusSetStatuses(shipmentStatusConfig.statusByMode?.SEA ?? []),
      ROA: cleanStatusSetStatuses(shipmentStatusConfig.statusByMode?.ROA ?? [])
    }
    const now = new Date()
    const db = FirebaseProvider.getInstance().getFirestore()
    const catalogRef = doc(db, `Companies/${companyId}/config/SHIPMENT_STATUS`)
    await setDoc(catalogRef, { statusByMode, updatedAt: Timestamp.fromDate(now) }, { merge: true })

    configs.value = {
      ...(configs.value || {}),
      SHIPMENT_STATUS: { statusByMode, updatedAt: nowTimestamp(now) }
    } as ConfigCatalogs
  }

  const updateStatusBaseField = async (companyId: string, field: string) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')
    if (!configs.value?.SHIPMENT_STATUS) throw new Error('No hay configuración de estados')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const now = new Date()
      configs.value = {
        ...configs.value,
        SHIPMENT_STATUS: {
          ...configs.value.SHIPMENT_STATUS,
          statusBaseField: field,
          updatedAt: nowTimestamp(now)
        }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/SHIPMENT_STATUS`)
      await updateDoc(catalogRef, {
        statusBaseField: field,
        updatedAt: Timestamp.fromDate(now)
      })

    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  const updatePurchaseOrderStatusOrder = async (companyId: string, statuses: any[]) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const updatedStatuses = statuses.map((status, index) => ({
        ...status,
        order: index
      }))

      const now = new Date()
      const timestamp = {
        seconds: Math.floor(now.getTime() / 1000),
        nanoseconds: (now.getTime() % 1000) * 1000000
      }

      configs.value = {
        ...configs.value,
        PURCHASE_ORDER_STATUS: {
          ...(configs.value?.PURCHASE_ORDER_STATUS || {}),
          status: updatedStatuses,
          updatedAt: timestamp
        }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/PURCHASE_ORDER_STATUS`)

      await setDoc(catalogRef, {
        status: updatedStatuses,
        updatedAt: Timestamp.fromDate(now)
      }, { merge: true })

    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  const updateContainerStatusOrder = async (companyId: string, statuses: any[]) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')
    if (!configs.value?.SHIPMENT_STATUS) throw new Error('No hay configuración de estados')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const updatedStatuses = statuses.map((status, index) => ({
        ...status,
        order: index
      }))

      const now = new Date()
      const timestamp = {
        seconds: Math.floor(now.getTime() / 1000),
        nanoseconds: (now.getTime() % 1000) * 1000000
      }

      configs.value = {
        ...configs.value,
        SHIPMENT_STATUS: {
          ...configs.value.SHIPMENT_STATUS,
          statusByContainer: updatedStatuses,
          updatedAt: timestamp
        } as any
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/SHIPMENT_STATUS`)
      await updateDoc(catalogRef, {
        statusByContainer: updatedStatuses,
        updatedAt: Timestamp.fromDate(now)
      })

    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  const updateChargeCategories = async (companyId: string, categories: ChargeCategory[]) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const updatedCategories = categories.map((cat, index) => ({
        ...cat,
        order: index
      }))

      const now = new Date()
      const timestamp = {
        seconds: Math.floor(now.getTime() / 1000),
        nanoseconds: (now.getTime() % 1000) * 1000000
      }

      configs.value = {
        ...configs.value,
        CHARGE_CATEGORIES_CONFIG: {
          ...(configs.value?.CHARGE_CATEGORIES_CONFIG || {}),
          categories: updatedCategories,
          updatedAt: timestamp
        }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/CHARGE_CATEGORIES_CONFIG`)
      await setDoc(catalogRef, {
        categories: updatedCategories,
        updatedAt: Timestamp.fromDate(now)
      }, { merge: true })
    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  const updateChargeCategoriesCompleteness = async (
    companyId: string,
    completeness: ChargeCategoriesCompletenessConfig
  ) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const now = new Date()
      configs.value = {
        ...configs.value,
        CHARGE_CATEGORIES_CONFIG: {
          categories: [],
          ...(configs.value?.CHARGE_CATEGORIES_CONFIG || {}),
          completeness
        }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/CHARGE_CATEGORIES_CONFIG`)
      await setDoc(catalogRef, { completeness, updatedAt: Timestamp.fromDate(now) }, { merge: true })
    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  /** Campos exigidos en cada cargo (ver @/utils/liquidation/requiredFields). */
  const updateRequiredChargeFields = async (companyId: string, requiredChargeFields: string[]) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const now = new Date()
      configs.value = {
        ...configs.value,
        CHARGE_CATEGORIES_CONFIG: {
          categories: [],
          ...(configs.value?.CHARGE_CATEGORIES_CONFIG || {}),
          requiredChargeFields
        }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/CHARGE_CATEGORIES_CONFIG`)
      await setDoc(catalogRef, { requiredChargeFields, updatedAt: Timestamp.fromDate(now) }, { merge: true })
    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  /**
   * Interruptor del Total Gastos en Destino. Qué categorías suma va en cada
   * categoría, que se guarda con `updateChargeCategories`.
   */
  const updateDestinationTotalEnabled = async (companyId: string, destinationTotalEnabled: boolean) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const now = new Date()
      configs.value = {
        ...configs.value,
        CHARGE_CATEGORIES_CONFIG: {
          categories: [],
          ...(configs.value?.CHARGE_CATEGORIES_CONFIG || {}),
          destinationTotalEnabled
        }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/CHARGE_CATEGORIES_CONFIG`)
      await setDoc(catalogRef, { destinationTotalEnabled, updatedAt: Timestamp.fromDate(now) }, { merge: true })
    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  /**
   * Marca del seguro presuntivo en el Total Gastos en Destino. Va aparte de
   * `updateDestinationTotalEnabled` porque no es una categoría: la prima la
   * calcula el motor y no vive en `categories`.
   */
  const updateDestinationTotalSeguroPresuntivo = async (
    companyId: string,
    destinationTotalIncludesSeguroPresuntivo: boolean
  ) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const now = new Date()
      configs.value = {
        ...configs.value,
        CHARGE_CATEGORIES_CONFIG: {
          categories: [],
          ...(configs.value?.CHARGE_CATEGORIES_CONFIG || {}),
          destinationTotalIncludesSeguroPresuntivo
        }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/CHARGE_CATEGORIES_CONFIG`)
      await setDoc(catalogRef, { destinationTotalIncludesSeguroPresuntivo, updatedAt: Timestamp.fromDate(now) }, { merge: true })
    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  const updateLiquidationConfig = async (companyId: string, config: LiquidationConfig) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const now = new Date()
      const timestamp = {
        seconds: Math.floor(now.getTime() / 1000),
        nanoseconds: (now.getTime() % 1000) * 1000000
      }

      configs.value = {
        ...configs.value,
        LIQUIDATION_CONFIG: { ...config, updatedAt: timestamp }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/LIQUIDATION_CONFIG`)
      // Sin merge: los grupos son objetos completos y un merge dejaría vivas
      // vigencias o agencias que el usuario acaba de eliminar.
      await setDoc(catalogRef, { ...config, updatedAt: Timestamp.fromDate(now) })
    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  /**
   * Decimales de la moneda local. Se editan en la tarjeta Moneda Local pero
   * siguen viviendo en `LIQUIDATION_CONFIG.documento.decimalesMonedaLocal`, que es
   * de donde los leen Cargos, el PDF, el Excel y el comparativo. Con merge: solo
   * se escribe este campo, el resto de la configuración de liquidación no se toca.
   */
  const updateLiquidationLocalDecimals = async (companyId: string, decimalesMonedaLocal: number | null) => {
    if (!companyId) throw new Error('No hay una compañía seleccionada')

    const previousState = JSON.parse(JSON.stringify(configs.value))

    try {
      const now = new Date()
      const stored = configs.value?.LIQUIDATION_CONFIG
      configs.value = {
        ...configs.value,
        LIQUIDATION_CONFIG: {
          ...stored,
          documento: { ...stored?.documento, decimalesMonedaLocal }
        }
      }

      const db = FirebaseProvider.getInstance().getFirestore()
      const catalogRef = doc(db, `Companies/${companyId}/config/LIQUIDATION_CONFIG`)
      await setDoc(
        catalogRef,
        { documento: { decimalesMonedaLocal }, updatedAt: Timestamp.fromDate(now) },
        { merge: true }
      )
    } catch (error) {
      configs.value = previousState
      throw error
    }
  }

  return {
    loading,
    error,
    configs,
    getItems,
    getMetadata,
    getShowChargesCatalog,
    getShowAll,
    getTransportersCount,
    getPayToCount,
    getChargeCategories,
    getActiveChargeCategories,
    getChargeCategoriesCompleteness,
    getRequiredChargeFields,
    getDestinationTotalEnabled,
    getDestinationTotalIncludesSeguroPresuntivo,
    getLiquidationConfig,
    loadConfigs,
    updateCatalogItem,
    toggleItem,
    toggleVisibility,
    updateStatusOrder,
    updateStatusOrderByMode,
    createStatusSet,
    updateStatusSetStatuses,
    deleteStatusSet,
    updateStatusBaseField,
    ensureShipmentStatusConfig,
    updateContainerStatusOrder,
    updatePurchaseOrderStatusOrder,
    updateChargeCategories,
    updateChargeCategoriesCompleteness,
    updateRequiredChargeFields,
    updateDestinationTotalEnabled,
    updateDestinationTotalSeguroPresuntivo,
    updateLiquidationConfig,
    updateLiquidationLocalDecimals
  }
}) 

// Interfaces
interface UpdateCatalogParams {
  companyId: string
  configKey: keyof ConfigCatalogs
  item: {
    value: string
    label: string
    isActived?: boolean
    trackCarrier?: string | null
  }
  itemsKey?: 'transporters' | 'payTo'
  field?: 'isActived' | 'visible'
}

interface UpdateCarrierParams {
  companyId: string
  section: 'transporters' | 'payTo'
  carrier: {
    value: string
    label: string
    isActived?: boolean
    trackCarrier?: string | null
  }
} 